import React, { FormEvent, useEffect, useState } from 'react';
import i18n from '@/modules/i18n';
import { useModalManager } from '@/components/common/Modal';
import SubmitModal from '@/components/ui/Modal/SubmitModal';
import ReactSelect5 from '@/components/common/ReactSelect5';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { plato_api_staffer_path } from '@/modules/routes';
import { queryClient } from '@/utils/ReactQuery';
import QueryKeys from '@/utils/QueryKeys';
import Icon from '@/components/ui/Icon';
import Text from '@/components/ui/Text';
import { COLORS, glyphFocusRing } from '@/components/ui/ui.constants';
import cn from '@/utils/cn';

interface CorrelationsSettingsModalProps {
  grade: number | null;
  onSaved: (grade: number) => void;
  programId: number;
  stafferId: number;
  state: string;
}

const LABEL_CLASSES = 'tw-w-[65px] tw-font-bold';
const GRADES = ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
const GRADE_OPTIONS = GRADES.map((label, value) => ({ label, value }));

const CorrelationsSettingsModal = ({
  grade,
  onSaved,
  programId,
  stafferId,
  state,
}: CorrelationsSettingsModalProps) => {
  const { isOpen, close, open } = useModalManager();
  const [selectedGrade, setSelectedGrade] = useState(grade);

  const openModal = () => {
    setSelectedGrade(grade);
    open();
  };

  const { isLoading, mutateAsync } = useMutation({
    mutationFn: ({ grade: newGrade }: { grade: number }) => (
      axios
        .patch(plato_api_staffer_path(stafferId), {
          correlations: { [programId]: newGrade },
        })
        .then(res => res.data)
    ),
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    await mutateAsync({ grade: selectedGrade });

    onSaved(selectedGrade);
    await queryClient.invalidateQueries([QueryKeys.Correlations, programId]);
    close();
  };

  useEffect(() => {
    if (grade === null) openModal();
  }, [grade]);

  return (
    <>
      <div className="tw-flex tw-items-center tw-justify-end tw-gap-1.5 tw-m-2.5">
        <Text size="sm">{i18n.t('state')}: {state}</Text>
        {' | '}
        <Text size="sm">{i18n.t('grade')}: {grade === null ? 'N/A' : GRADES[grade]}</Text>
        <button
          aria-label={i18n.t('edit_state_and_grade')}
          className={cn(glyphFocusRing, 'tw-inline-flex tw-bg-inherit tw-border-none tw-p-0 tw-cursor-pointer')}
          type="button"
          onClick={openModal}
        >
          <Icon fill={COLORS['tci-purple']} icon="Writing" size={16} />
        </button>
        <SubmitModal
          close={close}
          headerText={`${i18n.t('correlation')} ${i18n.t('settings')}`}
          isLoading={isLoading}
          isOpen={isOpen}
          isValid={selectedGrade !== null}
          submitText={i18n.t('save')}
          submittingText={`${i18n.t('saving')}...`}
          onSubmit={handleSubmit}
        >
          <div className="tw-flex tw-flex-col tw-gap-4 tw-min-h-[225px]">
            <div>
              <label className={LABEL_CLASSES} htmlFor="state">
                {i18n.t('state')}:
              </label>
              <span>{state}</span>
            </div>
            <div className="tw-flex tw-items-baseline">
              <label className={LABEL_CLASSES} htmlFor="grade">
                {i18n.t('grade')}:
              </label>
              <ReactSelect5
                className="tw-min-w-[100px]"
                defaultValue={GRADE_OPTIONS.find(opt => opt.value === selectedGrade)}
                dropdownHeightOverride={170}
                name="grade"
                options={GRADE_OPTIONS}
                onChange={({ value }) => setSelectedGrade(value)}
              />
            </div>
            <p className="tw-text-red tw-italic tw-text-xs">
              These settings apply to everywhere correlations can be seen throughout the website.
            </p>
          </div>
        </SubmitModal>
      </div>
    </>
  );
};

export default CorrelationsSettingsModal;
